import { DASHBOARD_BLOCK_TYPES, type DashboardBlockType } from './types/index.js';

/**
 * A page opened for one item (F-0621, concept-design-agency §21, AGM-42). A page whose
 * `scopeItemTypeKey` names an item type is opened for one item of that type, at
 * `pages/:pageId/items/:key`; its blocks bound to "the page's item" read that item, as the reader.
 * A work-area page (`scopeItemTypeKey` null) has no item, and no block on it may be bound to one.
 *
 * ⛔ The binding names no item: a configuration package ships the page with `itemSource: 'page'`,
 * so the same page serves every item of its type and no key is written into the page.
 */
export const PAGE_ITEM_SOURCE = 'page' as const;

/**
 * The block types that may read the page's item instead of a fixed `itemKey`. A block type joins
 * when it reads its item by key as the reader, the way the Action bar does.
 */
export const PAGE_ITEM_BLOCK_TYPES: readonly DashboardBlockType[] = [
  DASHBOARD_BLOCK_TYPES.ACTION_BAR,
  DASHBOARD_BLOCK_TYPES.REVIEW_CANVAS,
  DASHBOARD_BLOCK_TYPES.APPROVAL_PANEL,
  DASHBOARD_BLOCK_TYPES.ITEM_FIELDS,
  DASHBOARD_BLOCK_TYPES.NEXT_ACTION,
  DASHBOARD_BLOCK_TYPES.FILE_GALLERY,
  DASHBOARD_BLOCK_TYPES.COVERAGE_GRID,
  DASHBOARD_BLOCK_TYPES.TRAIL,
  DASHBOARD_BLOCK_TYPES.ITEM_QUESTIONS,
  DASHBOARD_BLOCK_TYPES.PROJECT_PROGRESS,
  DASHBOARD_BLOCK_TYPES.DESIGN_GRID,
];

/**
 * The block types that may list the children of the page's item (`parent: 'page'`) instead of the
 * whole work area's items: the Job page's Deliverables.
 */
export const PAGE_ITEM_CHILDREN_BLOCK_TYPES: readonly DashboardBlockType[] = [
  DASHBOARD_BLOCK_TYPES.WORK_ITEM_LIST,
];

/**
 * The item an item-scoped page is opened for, as `GET …/dashboard-pages/:pageId/items/:key` answers
 * it. The id is what a list of its children filters on; a guest's allowlist carries it too (AGM-19).
 */
export interface IPageScopeItem {
  id: string;
  key: string;
  title: string;
}

/**
 * PX-60 (I-2426): the items an item-scoped page opened without one offers its reader, as
 * `GET …/dashboard-pages/:pageId/items` answers: those of the page's type the reader reads, newest
 * first, at most `PAGE_SCOPE_CHOICES_MAX`; `total` counts them all.
 */
export interface IPageScopeChoices {
  items: IPageScopeItem[];
  total: number;
}

/** The most items a page opened without one lists to choose from. */
export const PAGE_SCOPE_CHOICES_MAX = 50;

/** The longest item-type key a page may be scoped to; item type keys are shorter. */
export const PAGE_SCOPE_ITEM_TYPE_KEY_MAX = 80;

export function isPageItemBlockType(type: string): boolean {
  return (PAGE_ITEM_BLOCK_TYPES as readonly string[]).includes(type);
}

/** Whether a block reads the page's item rather than a key of its own. */
export function readsPageItem(config: unknown): boolean {
  return (
    Boolean(config) &&
    typeof config === 'object' &&
    (config as { itemSource?: unknown }).itemSource === PAGE_ITEM_SOURCE
  );
}

/** Whether a list shows only the children of the page's item. */
export function listsPageItemChildren(config: unknown): boolean {
  return (
    Boolean(config) &&
    typeof config === 'object' &&
    (config as { parent?: unknown }).parent === PAGE_ITEM_SOURCE
  );
}

/**
 * The key a block reads: the page's item for a bound block (null while the page is opened without
 * one), else the block's own `itemKey`.
 */
export function resolveBlockItemKey(config: unknown, pageItemKey: string | null): string | null {
  if (readsPageItem(config)) return pageItemKey;
  const own = (config as { itemKey?: unknown } | null | undefined)?.itemKey;
  return typeof own === 'string' && own.trim() ? own.trim() : null;
}

/**
 * Why these blocks cannot be saved on a page of this scope, in words; empty when they can. A block
 * bound to the page's item needs a page that has one, a block type that reads an item, and no fixed
 * key beside the binding, which would leave two answers to "which item".
 */
export function pageItemBindingProblems(
  blocks: ReadonlyArray<{ type: string; title?: string | null; config?: unknown }>,
  scopeItemTypeKey: string | null,
): string[] {
  const problems: string[] = [];
  for (const block of blocks) {
    const name = block.title?.trim() || block.type;
    if (listsPageItemChildren(block.config)) {
      if (!(PAGE_ITEM_CHILDREN_BLOCK_TYPES as readonly string[]).includes(block.type)) {
        problems.push(
          `“${name}” cannot list the page’s item’s children: this block is not a list.`,
        );
      } else if (!scopeItemTypeKey) {
        problems.push(
          `“${name}” lists the children of the page’s item, but this page is not opened for an item. Scope the page to an item type, or list the whole work area.`,
        );
      }
    }
    if (!readsPageItem(block.config)) continue;
    if (!isPageItemBlockType(block.type)) {
      problems.push(`“${name}” cannot show the page’s item: this block does not read one item.`);
      continue;
    }
    if (!scopeItemTypeKey) {
      problems.push(
        `“${name}” shows the page’s item, but this page is not opened for an item. Scope the page to an item type, or choose the item in the block.`,
      );
    }
    const own = (block.config as { itemKey?: unknown }).itemKey;
    if (typeof own === 'string' && own.trim()) {
      problems.push(`“${name}” names an item and also shows the page’s item. Keep one.`);
    }
  }
  return problems;
}
